import * as sqlynx from "@ankoh/sqlynx-core";
import SQLYNX_MODULE_URL from "@ankoh/sqlynx-core/dist/sqlynx.wasm?url";
import { useRef } from "react";
import * as React from "react";

import { MAX_IDENTIFIERS_COUNT } from "pages/DevelopWorkspace/contexts/SQLynxContext/constants";
import { prepareDescriptor } from "pages/DevelopWorkspace/contexts/SQLynxContext/helpers/prepareDescriptor";
import { runWhenIdle } from "pages/DevelopWorkspace/contexts/SQLynxContext/helpers/runWhenIdle";

import AppCrash from "components/ErrorBoundary/AppCrash";
import LoadingOverlap from "components/LoadingOverlap/LoadingOverlap";

export const RESULT_OK = Symbol("RESULT_OK");
export const RESULT_ERROR = Symbol("RESULT_ERROR");

export type Result<ValueType> =
  | { type: typeof RESULT_OK; value: ValueType }
  | { type: typeof RESULT_ERROR; error: Error };

export interface InstantiationProgress {
  startedAt: Date;
  updatedAt: Date;
  bytesTotal: bigint;
  bytesLoaded: bigint;
}

export interface SchemaTable {
  tableName: string;
  columns: string[];
}

export interface SchemaDescriptor {
  databaseName: string;
  schemaName: string;
  tables: SchemaTable[];
}

const INITIAL_DESCRIPTOR_POOL_ID = 1;

interface Props {
  children: React.ReactElement;
}

const PROGRESS_CONTEXT = React.createContext<InstantiationProgress | null>(
  null
);

interface SQLynxContextType {
  sqlynx: sqlynx.SQLynx;
  catalog: sqlynx.SQLynxCatalog | null;
  addDescriptor: (descriptor: SchemaDescriptor) => void;
  descriptors: SchemaDescriptor[];
  sqlynxScript: sqlynx.SQLynxScript | null;
}

const MODULE_CONTEXT = React.createContext<SQLynxContextType>(
  {} as SQLynxContextType
);

export const SQLynxLoader: React.FC<Props> = (props: Props) => {
  const [module, setModule] = React.useState<Result<sqlynx.SQLynx> | null>(
    null
  );
  const [progress, setProgress] = React.useState<InstantiationProgress | null>(
    null
  );

  const catalogRef = useRef<sqlynx.SQLynxCatalog | null>(null);
  const scriptRef = useRef<sqlynx.SQLynxScript | null>(null);

  const poolIDRef = useRef<number>(INITIAL_DESCRIPTOR_POOL_ID);
  const descriptorsRef = useRef<SchemaDescriptor[]>([]);

  const createAndAddSqlynxDescriptor = (descriptor: SchemaDescriptor) => {
    if (catalogRef.current) {
      const schemaDescriptor = new sqlynx.proto.SchemaDescriptorT(
        descriptor.databaseName,
        descriptor.schemaName,
        descriptor.tables.map(table => {
          return new sqlynx.proto.SchemaTableT(
            0,
            table.tableName,
            table.columns.map(column => {
              return new sqlynx.proto.SchemaTableColumnT(column);
            })
          );
        })
      );

      catalogRef.current.addSchemaDescriptorT(
        poolIDRef.current,
        schemaDescriptor
      );
    }
  };

  const addDescriptor = (newDescriptor: SchemaDescriptor) => {
    // check if descriptor already exists
    // if no, update state and add to catalog
    const foundDescriptor = descriptorsRef.current.find(
      d =>
        d.databaseName === newDescriptor.databaseName &&
        d.schemaName === newDescriptor.schemaName
    );

    if (foundDescriptor) {
      return;
    }

    const descriptor = prepareDescriptor(newDescriptor);

    if (!descriptor) {
      // ignore this descriptor
      return;
    }

    // total identifiers number in all descriptors
    const allSchemasIdentifiersCount = descriptorsRef.current.reduce(
      (acc, d) => {
        return (
          acc +
          d.tables.reduce((acc, t) => {
            return acc + t.columns.length + 1; // +1 because table name is also an identifier
          }, 0)
        );
      },
      0
    );

    if (allSchemasIdentifiersCount > MAX_IDENTIFIERS_COUNT) {
      // keep only information_schema descriptor as it is remains the same
      const informationSchemaDescriptor = descriptorsRef.current.find(
        d => d.databaseName === "" && d.schemaName === "information_schema"
      );

      // clean internal storage and sqlynx descriptor pool
      descriptorsRef.current = [];
      catalogRef.current?.dropDescriptorPool(poolIDRef.current);

      poolIDRef.current += 1;
      // we have to create a new pool, it's the only workaround
      // the previous dropped one still exists in memory
      catalogRef.current?.addDescriptorPool(poolIDRef.current, 10);

      if (informationSchemaDescriptor) {
        // push information_schema descriptor to a new pool
        descriptorsRef.current.push(informationSchemaDescriptor);
        createAndAddSqlynxDescriptor(informationSchemaDescriptor);
      }
    }

    // push descriptor to internal storage
    descriptorsRef.current.push(descriptor);
    runWhenIdle(() => {
      // push descriptor to SQLynx descriptor pool
      createAndAddSqlynxDescriptor(descriptor);
    }, 2000);
  };

  React.useEffect(() => {
    const now = new Date();
    const internal: InstantiationProgress = {
      startedAt: now,
      updatedAt: now,
      bytesTotal: BigInt(0),
      bytesLoaded: BigInt(0),
    };
    // Fetch an url with progress tracking
    const fetchWithProgress = async (url: string) => {
      // Try to determine file size
      const request = new Request(url);
      const response = await fetch(request);
      const contentLengthHdr = response.headers.get("content-length");
      const contentLength = contentLengthHdr
        ? parseInt(contentLengthHdr, 10) || 0
        : 0;

      const now = new Date();
      internal.startedAt = now;
      internal.updatedAt = now;
      internal.bytesTotal = BigInt(contentLength) || BigInt(0);
      internal.bytesLoaded = BigInt(0);
      const tracker = {
        transform(chunk: Uint8Array, ctrl: TransformStreamDefaultController) {
          const prevUpdate = internal.updatedAt;
          internal.updatedAt = now;
          internal.bytesLoaded += BigInt(chunk.byteLength);
          if (now.getTime() - prevUpdate.getTime() > 20) {
            setProgress(_ => ({ ...internal }));
          }
          ctrl.enqueue(chunk);
        },
      };
      const ts = new TransformStream(tracker);
      return new Response(response.body?.pipeThrough(ts), response);
    };
    const instantiate = async () => {
      try {
        const instance = await sqlynx.SQLynx.create(
          async (imports: WebAssembly.Imports) => {
            return await WebAssembly.instantiateStreaming(
              fetchWithProgress(SQLYNX_MODULE_URL),
              imports
            );
          }
        );

        setProgress(_ => ({
          ...internal,
          updatedAt: new Date(),
        }));

        const lnx = instance;

        const catalog = lnx.createCatalog();
        catalog.addDescriptorPool(INITIAL_DESCRIPTOR_POOL_ID, 10);

        scriptRef.current = lnx.createScript(catalog, 2);

        catalogRef.current = catalog;

        setModule({
          type: RESULT_OK,
          value: instance!,
        });
      } catch (e: any) {
        console.error(e);
        setModule({
          type: RESULT_ERROR,
          error: e!,
        });
      }
    };
    instantiate();
  }, []);

  if (!module) {
    return <LoadingOverlap isLoading={true} />;
  }

  if (module.type === RESULT_ERROR) {
    return <AppCrash />;
  }

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const contextValue = {
    sqlynx: module.value,
    catalog: catalogRef.current,
    addDescriptor,
    descriptors: descriptorsRef.current,
    sqlynxScript: scriptRef.current,
  };

  return (
    <PROGRESS_CONTEXT.Provider value={progress}>
      <MODULE_CONTEXT.Provider value={contextValue}>
        {props.children}
      </MODULE_CONTEXT.Provider>
    </PROGRESS_CONTEXT.Provider>
  );
};

export const useSQLynx = (): SQLynxContextType =>
  React.useContext(MODULE_CONTEXT);
